import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';

const CUSTOMER_TOKEN_KEY = 'token';
const BARISTA_TOKEN_KEY = 'baristaToken';
const USER_CACHE_KEY = 'authUserCache';
const BARISTA_PROFILE_KEY = 'barista';

let memoryCustomerToken: string | null = null;
let memoryBaristaToken: string | null = null;
let memoryUserCache: string | null = null;
let memoryBaristaProfile: string | null = null;
let hydrated = false;
let hydratePromise: Promise<void> | null = null;

function readLocal(key: string): string | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeLocal(key: string, value: string | null) {
  if (typeof localStorage === 'undefined') return;
  try {
    if (value) localStorage.setItem(key, value);
    else localStorage.removeItem(key);
  } catch {
    // WebView storage can be unavailable; memory + Preferences still hold the token.
  }
}

async function writeNative(key: string, value: string | null) {
  if (!Capacitor.isNativePlatform()) return;
  try {
    if (value) {
      await Preferences.set({ key, value });
    } else {
      await Preferences.remove({ key });
    }
  } catch {
    // Ignore native storage failures; in-memory token still works for this session.
  }
}

async function readNative(key: string): Promise<string | null> {
  if (!Capacitor.isNativePlatform()) return null;
  try {
    const result = await Preferences.get({ key });
    return result.value || null;
  } catch {
    return null;
  }
}

function pickStoredValue(memory: string | null, local: string | null, native: string | null): string | null {
  return memory || native || local || null;
}

export function isAuthHydrated(): boolean {
  return hydrated;
}

export async function hydrateAuthTokens(): Promise<void> {
  if (hydrated) return;
  if (hydratePromise) return hydratePromise;
  hydratePromise = (async () => {
    const localCustomer = readLocal(CUSTOMER_TOKEN_KEY);
    const localBarista = readLocal(BARISTA_TOKEN_KEY);
    const localUser = readLocal(USER_CACHE_KEY);
    const localProfile = readLocal(BARISTA_PROFILE_KEY);

    const [nativeCustomer, nativeBarista, nativeUser, nativeProfile] = await Promise.all([
      readNative(CUSTOMER_TOKEN_KEY),
      readNative(BARISTA_TOKEN_KEY),
      readNative(USER_CACHE_KEY),
      readNative(BARISTA_PROFILE_KEY),
    ]);

    memoryCustomerToken = pickStoredValue(memoryCustomerToken, localCustomer, nativeCustomer);
    memoryBaristaToken = pickStoredValue(memoryBaristaToken, localBarista, nativeBarista);
    memoryUserCache = pickStoredValue(memoryUserCache, localUser, nativeUser);
    memoryBaristaProfile = pickStoredValue(memoryBaristaProfile, localProfile, nativeProfile);

    writeLocal(CUSTOMER_TOKEN_KEY, memoryCustomerToken);
    writeLocal(BARISTA_TOKEN_KEY, memoryBaristaToken);
    writeLocal(USER_CACHE_KEY, memoryUserCache);
    writeLocal(BARISTA_PROFILE_KEY, memoryBaristaProfile);

    await Promise.all([
      memoryCustomerToken && memoryCustomerToken !== nativeCustomer
        ? writeNative(CUSTOMER_TOKEN_KEY, memoryCustomerToken)
        : Promise.resolve(),
      memoryBaristaToken && memoryBaristaToken !== nativeBarista
        ? writeNative(BARISTA_TOKEN_KEY, memoryBaristaToken)
        : Promise.resolve(),
      memoryUserCache && memoryUserCache !== nativeUser
        ? writeNative(USER_CACHE_KEY, memoryUserCache)
        : Promise.resolve(),
      memoryBaristaProfile && memoryBaristaProfile !== nativeProfile
        ? writeNative(BARISTA_PROFILE_KEY, memoryBaristaProfile)
        : Promise.resolve(),
    ]);

    hydrated = true;
  })();
  await hydratePromise;
}

export function getCustomerTokenSync(): string | null {
  return memoryCustomerToken || readLocal(CUSTOMER_TOKEN_KEY);
}

export function getBaristaTokenSync(): string | null {
  return memoryBaristaToken || readLocal(BARISTA_TOKEN_KEY);
}

export function getCachedUserRawSync(): string | null {
  return memoryUserCache || readLocal(USER_CACHE_KEY);
}

export function getBaristaProfileRawSync(): string | null {
  return memoryBaristaProfile || readLocal(BARISTA_PROFILE_KEY);
}

export async function setCustomerToken(token: string | null): Promise<void> {
  memoryCustomerToken = token;
  writeLocal(CUSTOMER_TOKEN_KEY, token);
  await writeNative(CUSTOMER_TOKEN_KEY, token);
}

export async function setBaristaToken(token: string | null): Promise<void> {
  memoryBaristaToken = token;
  writeLocal(BARISTA_TOKEN_KEY, token);
  await writeNative(BARISTA_TOKEN_KEY, token);
}

export async function setCachedUserRaw(raw: string | null): Promise<void> {
  memoryUserCache = raw;
  writeLocal(USER_CACHE_KEY, raw);
  await writeNative(USER_CACHE_KEY, raw);
}

export async function setBaristaProfile(profile: unknown | null): Promise<void> {
  const raw = profile ? JSON.stringify(profile) : null;
  memoryBaristaProfile = raw;
  writeLocal(BARISTA_PROFILE_KEY, raw);
  await writeNative(BARISTA_PROFILE_KEY, raw);
}

export function getAuthHeaderForPath(pathname: string): string | null {
  const onStaffConsole = pathname === '/barista' || pathname.startsWith('/barista/');
  const baristaToken = getBaristaTokenSync();
  const customerToken = getCustomerTokenSync();
  if (onStaffConsole && baristaToken) return baristaToken;
  if (customerToken) return customerToken;
  if (baristaToken) return baristaToken;
  return null;
}

export { USER_CACHE_KEY };
